import { Controller } from "@hotwired/stimulus"

/**
 * Dark Mode Controller
 *
 * Handles theme toggling between light and dark modes.
 * Initial theme is set synchronously in application.js (before imports)
 * to prevent flash of wrong theme.
 */
export default class extends Controller {
  static targets = ["toggle", "lightIcon", "darkIcon"]

  connect() {
    this.syncUI()
    this.#listenForSystemChanges()
  }

  toggle() {
    const isDark = !this.#isDark()
    localStorage.setItem("theme-mode", isDark ? "dark" : "light")
    this.#apply(isDark)
    this.syncUI()
    this.#dispatchChange(isDark)
  }

  syncUI() {
    const isDark = this.#isDark()

    // Update checkbox state
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = isDark
    }

    // Update icons using targets (more efficient than querySelector)
    if (this.hasLightIconTarget && this.hasDarkIconTarget) {
      this.lightIconTarget.classList.toggle("hidden", isDark)
      this.darkIconTarget.classList.toggle("hidden", !isDark)
    }
  }

  // Private methods

  #isDark() {
    const stored = localStorage.getItem("theme-mode")
    if (stored) return stored === "dark"
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
  }

  #apply(isDark) {
    document.documentElement.classList.toggle("dark", isDark)
  }

  #listenForSystemChanges() {
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      // Only react to system changes if no stored preference
      if (!localStorage.getItem("theme-mode")) {
        this.#apply(this.#isDark())
        this.syncUI()
      }
    })
  }

  #dispatchChange(isDark) {
    window.dispatchEvent(new CustomEvent("theme-changed", { detail: { isDark } }))
  }
}
